import React, { useState, Fragment } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { ArrowForward, ContentCopy } from '@mui/icons-material';
import { Col, Row } from 'antd';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import PasswordRevealInline from 'components/PasswordRevealInline';
import { createKeystore } from 'store/keystore/thunks';
import { getUserIntegration } from 'utils';
import { v4 } from 'uuid';
import Loading from 'components/Loading';

const UserInfoCard = ({ 
    data, 
    userPath = 'user',
    schoolPath = 'school',
    districtPath = 'district',
    showSchoolDistrict = true 
}) => {
    const [loader, setLoader] = useState(false);
    const [copied, setCopied] = useState(false);
    
    const user = get(data, userPath);
    const school = get(data, schoolPath);
    const district = get(data, districtPath);
    
    const openLoginInNewWindow = async () => {
        setLoader(true);
        const key = v4();
        await createKeystore(get(data, `${userPath}.id`, ''), key);
        const url = `${window.location.origin}?key=${key}`;
        window.open(url, '_blank', 'toolbar=0,location=0,menubar=0');
        setLoader(false);
    };
    
    const copyUserInfo = async () => {
        const userInfo = [
            'User on OWE Platform',
            '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
            `Id: ${get(data, `${userPath}.id`, '')}`,
            `Create at: ${get(data, `${userPath}.createdAt`, null) ? moment(parseInt(get(data, `${userPath}.createdAt`, ''))).format("MM/DD/YYYY") : 'N/A'}`,
            `First Name: ${get(data, `${userPath}.firstName`, '')}`,
            `Last name: ${get(data, `${userPath}.lastName`, '')}`,
            `Role: ${get(data, `${userPath}.role`, '')}`,
            `User Name: ${get(data, `${userPath}.username`, '')}`,
            `Password: (use Show password in UI — not copied)`,
            `School: ${get(data, `${userPath}.school`, '')}`,
            `District: ${get(data, `${userPath}.district`, '')}`,
            `Classes: ${get(data, "classes.length", 0)}`,
            `Integration: ${getUserIntegration(user)}`
        ].join('\n');
        
        try {
            await navigator.clipboard.writeText(userInfo);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    
    if (isEmpty(user)) return null;
    
    const userCardContent = (
        <Col span={12} style={{ paddingRight: "8px" }}>
            <div style={{
                background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                borderRadius: '8px',
                padding: '12px',
                color: 'white',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                border: '1px solid rgba(255,255,255,0.1)',
                height: 'fit-content',
            }}>
                <div style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    marginBottom: '12px',
                    textAlign: 'center',
                    color: '#ecf0f1',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                }}>
                    <span>User on OWE Platform</span>
                    <Tooltip title={copied ? "Copied!" : "Copy user information"} arrow placement="top">
                        <IconButton
                            onClick={copyUserInfo}
                            size="small"
                            style={{
                                color: '#ecf0f1',
                                padding: '4px'
                            }}
                        >
                            <ContentCopy style={{ fontSize: '16px' }} />
                        </IconButton>
                    </Tooltip>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft:56, paddingRight:56 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>Id:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', fontFamily: 'monospace', textAlign: 'right', wordBreak: 'break-all' }}>{get(data, `${userPath}.id`, '')}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>Create at:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(data, `${userPath}.createdAt`, null) ? moment(parseInt(get(data, `${userPath}.createdAt`, ''))).format("MM/DD/YYYY") : null}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>First Name:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(data, `${userPath}.firstName`, '')}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>Last name:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(data, `${userPath}.lastName`, '')}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>Role:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(data, `${userPath}.role`, '')}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>User Name:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right', wordBreak: 'break-all' }}>{get(data, `${userPath}.username`, '')}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>Password:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right', wordBreak: 'break-all' }}><PasswordRevealInline userId={get(data, `${userPath}.id`)} username={get(data, `${userPath}.username`)} /></span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>School:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(data, `${userPath}.school`) || '-'}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>District:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(data, `${userPath}.district`) || '-'}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>Classes:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(data, "classes.length", 0)}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>Integration:</span>
                        <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{getUserIntegration(user)}</span>
                    </div>
                </div>
                
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <Button 
                        color="primary" 
                        variant="contained" 
                        onClick={openLoginInNewWindow}
                        style={{
                            background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontWeight: '600',
                            textTransform: 'none',
                            fontSize: '11px',
                            boxShadow: '0 2px 8px rgba(23, 162, 184, 0.3)',
                            border: 'none',
                            minWidth: '120px',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #138496 0%, #117a8b 100%)',
                                boxShadow: '0 4px 12px rgba(23, 162, 184, 0.4)'
                            }
                        }}
                    >
                        {loader && <Loading size={12} white />}
                        Login to account
                        <ArrowForward
                            style={{
                                fontSize: "8px",
                                marginLeft: "4px",
                            }}
                        />
                    </Button>
                </div>
            </div>
        </Col>
    );
    
    if (!showSchoolDistrict) {
        return userCardContent;
    }
    
    return (
        <Row style={{ width: "100%" }}>
            {userCardContent}
            {!isEmpty(school) && (
                <Col span={12} style={{ paddingLeft: "8px" }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: 'white',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        height: '100%'
                    }}>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: '700',
                            marginBottom: '12px',
                            textAlign: 'center',
                            color: '#ecf0f1',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            School & District
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: 56, paddingRight: 56 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>School:</span>
                                <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(school, "displayName", '')}</span>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <span style={{ color: '#bdc3c7', fontSize: '11px', fontWeight: '500', minWidth: '80px' }}>District:</span>
                                <span style={{ color: '#ecf0f1', fontSize: '11px', textAlign: 'right' }}>{get(district, "displayName", '')}</span>
                            </div>
                        </div>
                    </div>
                </Col>
            )}
        </Row>
    );
};

export default UserInfoCard;
